import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import LeaseSeason from '@app/models/lease-season';
import LeaseSeasonViewModel from '@app/models/lease-season-view-model';
import ValidationResultModel from '@app/models/validation-result-model';
import { DateService, LogService, ValueService } from '@app/services';
import { Subject, Subscription } from 'rxjs';
import { UserNamePipe } from '../../../common-components/pipes/user-name.pipe';
import { ActionButtonComponent } from '../../../common-components/action-button/action-button.component';
import { ErrorListComponent } from '../../../common-components/error-list/error-list.component';
import { OADateInputComponent } from '../../../common-components/oadate-input/oadate-input.component';
import { FormsModule } from '@angular/forms';
import { OAPopoverComponent } from '../../../common-components/oapopover/oapopover.component';
import { NgIf, AsyncPipe, DatePipe } from '@angular/common';

/**
 * A lightweight form for modifying a LeaseSeason
 * 
 * <lease-season-form
      (cancel)="onLeaseSeasonFormCancel()"
      (save)="onLeaseSeasonFormSave($event)"
      [value]="selectedLeaseSeason"
    >
 * </lease-season-form>
 */
@Component({
    selector: "lease-season-form",
    templateUrl: "./lease-season-form.component.html",
    styleUrls: ["./lease-season-form.component.less"],
    imports: [NgIf, OAPopoverComponent, FormsModule, OADateInputComponent, ErrorListComponent, ActionButtonComponent, AsyncPipe, DatePipe, UserNamePipe]
})
export class LeaseSeasonFormComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'LeaseSeasonFormComponent';
  /**
   * Private field for verbose logging
   */
  private _verboseLogging = false;

  /**
   * Subject to control observable completion
   */
  private unSubSub$ = new Subject<void>();

  @Output()
  public validChange = new EventEmitter<boolean>();

  @Output()
  public save = new EventEmitter<LeaseSeasonViewModel>();

  @Output()
  public cancel = new EventEmitter<void>();

  /**
   * Field indicationg if the form is valid
   */
  private _valid = false;

  private routerEvent$: Subscription;

  private _value: LeaseSeasonViewModel = null;

  @Input()
  public showTitle = true;

  public dateValidation: ValidationResultModel;
  public validations: ValidationResultModel;
  public fieldValidationResult: Record<string, ValidationResultModel> = {};

  constructor(
    private log: LogService,
    private dateService: DateService,
    private valueService: ValueService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.subscribeRouterEvents();
  }

  ngOnInit() {
    this.eval();
  }

  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();

    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  /**
     * Subscribes to Router Events and sets the _verboseLogging flag
     
      Add to Constructor:
        this.subscribeRouterEvents();
  
      Add Imports:
       import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart} from @angular/router;
       import { Subscription } from "rxjs";
       import { ValueService } from @app/services/value.service;
  
      Add Injections:
          private router: Router,
          private route: ActivatedRoute,
          private valueService: ValueService        
      
      Add to ngOnDestroy():
        if (this.routerEvent$) {
          this.routerEvent$.unsubscribe();
        }    
     */
  private subscribeRouterEvents() {
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log(`${this._componentName}.CTOR router.events NavigationEnd:`, event);

        console.log(
          `${this._componentName}.CTOR router.events NavigationEnd route.snapshot.queryParamMap=`,
          this.route.snapshot.queryParamMap
        );

        if (this.route.snapshot.queryParamMap.has('verboseLogging')) {
          if (this.valueService.getBoolean(this.route.snapshot.queryParamMap.get('verboseLogging')) == true) {
            this._verboseLogging = true;
          } else {
            this._verboseLogging = false;
          }
        }
      }
    });
  }

  public onChange(event: any) {
    console.log(`${this._componentName}.onChange event=`, event);

    this.eval();
  }

  /**
   * Evaluates if the data in the component (this.lease) is valid and updates the "valid" BehaviorSubject
   * @returns
   */
  public eval() {
    console.log(`${this._componentName}.eval()`);

    let b = true;

    let validations = {
      issues: [],
    } as ValidationResultModel;

    if (!this.value) {
      b = false;
      validations.issues.push('Empty lease property.');
    } else {
      if (!this.value.name || !this.value.description) {
        b = false;
        validations.issues.push('Lease requires a name and description.');
      }
    }

    this.dateValidation = null;

    var dateValidations = {
      isValid: true,
      issues: [],
    } as ValidationResultModel;

    if (this.value && !this.value.startDateTime) {
      dateValidations.isValid = false;
      dateValidations.issues.push('Available Date not set.');
    }

    if (this.value && !this.value.endDateTime) {
      dateValidations.isValid = false;
      dateValidations.issues.push('End Date not set.');
    }

    if (this.value && this.value.startDateTime && this.value.endDateTime) {
      if (this.dateService.isDateGreaterThan(this.value.startDateTime, this.value.endDateTime)) {
        dateValidations.isValid = false;
        dateValidations.issues.push("Start Date cannot occur after the 'End Date'.");
      }
    }

    this.dateValidation = dateValidations;
    console.log(`${this._componentName}.eval dateValidation:`, this.dateValidation);

    if (!dateValidations.isValid) {
      b = false;
    }

    validations.isValid = b;

    this.validations = validations;

    console.log(`${this._componentName}.eval - b5859b - `);

    if (this.fieldValidationResult['name'] && !this.fieldValidationResult['name'].isValid) {
      b = false;
    }

    if (this.valid != b) {
      console.log(`${this._componentName}.eval emitting - b5859b -  ${b}`);

      this.validChange.emit(b);
      this.valid = b;
    }

    return b;
  }

  public get value(): LeaseSeasonViewModel {
    return this._value;
  }

  @Input()
  public set value(v: LeaseSeasonViewModel) {
    this._value = v;
    console.log(`${this._componentName}.value set v=`, v);
    this.eval();
  }

  public get valid(): boolean {
    return this._valid;
  }
  public set valid(v: boolean) {
    this._valid = v;
    console.log(`${this._componentName}.valid set v=`, v);
  }

  public onSaveButtonMouseEnter(evt: MouseEvent) {
    console.log(`${this._componentName}.onSaveButtonMouseEnter evt=`, evt);
  }

  public onSaveButtonMouseLeave(evt: MouseEvent) {
    console.log(`${this._componentName}.onSaveButtonMouseLeave evt=`, evt);
  }

  public onSaveClick(evt: PointerEvent) {
    console.log(`${this._componentName}.onSaveClick evt=`, evt);

    this.save.emit(this.value);
  }

  public onCancelClick(evt: PointerEvent) {
    this.cancel.emit();
  }
}
